import type { SavedContact } from "../types";
import { storage, KEYS } from "./storage";
import { localClientState } from "../state/localClientState";

function readContacts(): SavedContact[] {
  return storage.get<SavedContact[]>(KEYS.SAVED_CONTACTS, []);
}

function writeContacts(contacts: SavedContact[]): void {
  storage.set(KEYS.SAVED_CONTACTS, contacts);
}

export const contactsService = {
  async save(
    companyId: string,
    options: { eventId?: string; notes?: string; userId?: string } = {},
  ): Promise<SavedContact> {
    const eventId = options.eventId ?? localClientState.getSelectedEventId() ?? "";
    const userId = options.userId ?? localClientState.getUserProfile().id;
    const contacts = readContacts();

    const existing = contacts.find(
      (c) => c.companyId === companyId && c.eventId === eventId,
    );
    if (existing) return existing;

    const contact: SavedContact = {
      id: `sc-${Date.now()}`,
      userId,
      eventId,
      companyId,
      savedAt: new Date().toISOString(),
      notes: options.notes,
    };
    writeContacts([contact, ...contacts]);
    return contact;
  },

  async listSaved(eventId?: string): Promise<SavedContact[]> {
    const contacts = readContacts();
    if (eventId) return contacts.filter((c) => c.eventId === eventId);
    return contacts;
  },

  isSaved(companyId: string, eventId?: string): boolean {
    const contacts = readContacts();
    return contacts.some(
      (c) => c.companyId === companyId && (!eventId || c.eventId === eventId),
    );
  },

  async updateNotes(companyId: string, notes: string, eventId?: string): Promise<void> {
    const contacts = readContacts();
    const next = contacts.map((c) => {
      if (c.companyId !== companyId) return c;
      if (eventId && c.eventId !== eventId) return c;
      return { ...c, notes };
    });
    writeContacts(next);
  },

  async remove(companyId: string, eventId?: string): Promise<void> {
    const contacts = readContacts();
    writeContacts(
      contacts.filter(
        (c) =>
          !(c.companyId === companyId && (!eventId || c.eventId === eventId)),
      ),
    );
  },
};
